import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../../store/store';
import { fetchUserSession } from '../../../../store/AuthSlice';
import { getFromLocalStorage } from '../../../../store/UserSlice';
import { clearPersonalDiscounts, fetchProducts } from '../../../../store/ProductSlice';
import { getCsrfToken } from '../../../../utils/helpers';
import './authPopupContent.scss';

export type AuthPopupContentProps = {
    onClose: () => void;
    onRequestCode?: (phoneE164: string) => void | Promise<void>;
};
type StatusKind = 'error' | 'info' | 'success';
type StatusState = { kind: StatusKind; text: string } | null;
type ParseResult = {
    national: string; // 0..10 digits
    nationalCaretIndex: number; // 0..national.length
};
type Step = 'phone' | 'code';
type PrefixMode = 'none' | 'plus' | 'country';

const RESEND_SECONDS = 120 as const;
const CHANNEL = 'sms' as const;
const OTP_LEN = 6 as const;
const IS_DEV = process.env.NODE_ENV === 'development';

const digitsOnly = (v: string) => v.replace(/\D/g, '');
const formatRuPhone = (national10: string): string => {
    const n = national10.slice(0, 10);
    const a = n.slice(0, 3);
    const b = n.slice(3, 6);
    const c = n.slice(6, 8);
    const d = n.slice(8, 10);
    let out = '+7';
    if (n.length > 0) out += ' ';
    if (a) out += a;
    if (n.length > 3) out += ' ';
    if (b) out += b;
    if (n.length > 6) out += ' ';
    if (c) out += c;
    if (n.length > 8) out += ' ';
    if (d) out += d;
    return out;
};
const formatRuPhoneParens = (national10: string): string => {
    const n = national10.slice(0, 10).padEnd(10, '0');
    const a = n.slice(0, 3);
    const b = n.slice(3, 6);
    const c = n.slice(6, 8);
    const d = n.slice(8, 10);
    return `+7 (${a}) ${b}-${c}-${d}`;
};
const redactOtpLike = (msg: string): string => msg.replace(/\b\d{4,6}\b/g, '••••');
const formatTimer = (seconds: number): string => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    const mm = m.toString().padStart(2, '0');
    const ss = s.toString().padStart(2, '0');
    return `${mm}:${ss}`;
};
const countDigitsBeforeCaret = (value: string, caretPos: number): number => {
    let cnt = 0;
    const end = Math.max(0, Math.min(caretPos, value.length));
    for (let i = 0; i < end; i++) {
        if (/\d/.test(value[i])) cnt++;
    }
    return cnt;
};
const clamp = (n: number, min: number, max: number): number => Math.max(min, Math.min(max, n));
const parsePhoneFromInput = (value: string, caretPos: number): ParseResult => {
    const digitsAll = digitsOnly(value);
    const digitsBefore = countDigitsBeforeCaret(value, caretPos);
    const trimmed = value.trim();
    const startsPlus = trimmed.startsWith('+');
    const firstDigit = digitsAll[0] ?? '';
    // если пользователь начал с "+", но дальше НЕ 7/8 - сохраняем только "+"
    if (startsPlus && digitsAll.length > 0 && firstDigit !== '7' && firstDigit !== '8') {
        return { national: '', nationalCaretIndex: 0 };
    }
    // префикс "7" или "8" считаем префиксом страны (и в формате "+7", и в сыром вводе)
    const shouldStripPrefix = firstDigit === '7' || firstDigit === '8';
    const strip = shouldStripPrefix ? 1 : 0;
    const national = digitsAll.slice(strip, strip + 10);
    const nationalCaretIndex = clamp(digitsBefore - strip, 0, national.length);
    return { national, nationalCaretIndex };
};
const caretPosFromNationalIndex = (formatted: string, nationalIdx: number): number => {
    if (!formatted) return 0;
    if (nationalIdx <= 0) return formatted.startsWith('+7 ') ? 3 : formatted.length;
    let seenNational = 0;
    let skippedCountry = false;
    for (let i = 0; i < formatted.length; i++) {
        const ch = formatted[i];
        if (/\d/.test(ch)) {
            if (!skippedCountry) {
                skippedCountry = true;
                continue;
            }
            seenNational++;
            if (seenNational === nationalIdx) return i + 1;
        }
    }
    return formatted.length;
};
const isProfileComplete = (u: any): boolean => {
    const hasText = (v: any) => typeof v === 'string' && v.trim().length > 0;
    const lastNameOk = hasText(u?.lastName);
    const firstNameOk = hasText(u?.firstName);
    const addressOk = u != null && Object.prototype.hasOwnProperty.call(u, 'address') && u.address != null;
    return lastNameOk && firstNameOk && addressOk;
};

const AuthPopupContent = ({ onClose, onRequestCode }: AuthPopupContentProps) => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch<AppDispatch>();
    const phoneInputRef = useRef<HTMLInputElement | null>(null);
    const otpInputRef = useRef<HTMLInputElement | null>(null);
    const abortRef = useRef<AbortController | null>(null);
    const pendingCaretPosRef = useRef<number | null>(null);
    const [step, setStep] = useState<Step>('phone');
    const [national, setNational] = useState<string>('');
    const [prefixMode, setPrefixMode] = useState<PrefixMode>('none');
    const [agree, setAgree] = useState<boolean>(false);
    const [touchedPhone, setTouchedPhone] = useState<boolean>(false);
    const [phoneError, setPhoneError] = useState<string>('');
    const [phoneStatus, setPhoneStatus] = useState<StatusState>(null);
    const [code, setCode] = useState<string>('');
    const [codeError, setCodeError] = useState<string>('');
    const [devCodeHint, setDevCodeHint] = useState<string>('');
    const [otpFocused, setOtpFocused] = useState<boolean>(false);
    const [busy, setBusy] = useState<'none' | 'request' | 'verify' | 'resend'>('none');
    const [resendTimer, setResendTimer] = useState<number>(0);

    const formattedPhone = useMemo(() => {
        if (national) return formatRuPhone(national);
        if (prefixMode === 'country') return '+7 ';
        if (prefixMode === 'plus') return '+';
        return '';
    }, [national, prefixMode]);

    const isPhoneComplete = national.length === 10;
    const phoneE164 = useMemo(() => (isPhoneComplete ? `+7${national}` : ''), [isPhoneComplete, national]);
    const phoneForSubtitle = useMemo(() => (isPhoneComplete ? formatRuPhoneParens(national) : '+7 (000) 000-00-00'), [isPhoneComplete, national]);
    const canSubmitPhone = isPhoneComplete && agree && busy === 'none' && resendTimer <= 0;
    const canSubmitCode = code.length === OTP_LEN && agree && busy === 'none';

    // Фокус при открытии
    useEffect(() => {
        const t = window.setTimeout(() => {
            if (step === 'phone') phoneInputRef.current?.focus();
            else otpInputRef.current?.focus();
        }, 60);
        return () => window.clearTimeout(t);
    }, [step]);

    // Таймер ресенда
    useEffect(() => {
        if (resendTimer <= 0) return;
        const interval = window.setInterval(() => {
            setResendTimer((prev) => (prev > 0 ? prev - 1 : 0));
        }, 1000);
        return () => window.clearInterval(interval);
    }, [resendTimer]);

    // Abort fetch на размонтирование
    useEffect(() => {
        return () => {
            abortRef.current?.abort();
        };
    }, []);

    useEffect(() => {
        if (step !== 'code') return;
        if (!window.isSecureContext) return;
        if (code.length > 0) return;
        const nav = navigator as unknown as {
            credentials?: {
                get?: (options: unknown) => Promise<unknown>;
            };
        };
        if (!nav.credentials?.get) return;
        const controller = new AbortController();
        nav.credentials
            .get({ otp: { transport: ['sms'] }, signal: controller.signal } as unknown)
            .then((cred) => {
                const rec = cred as { code?: unknown } | null;
                const c = rec && typeof rec.code === 'string' ? rec.code : '';
                const next = digitsOnly(c).slice(0, OTP_LEN);
                if (next.length !== OTP_LEN) return;
                setCode(next);
                setCodeError('');
            })
            .catch(() => {
                // ignore
            });
        return () => controller.abort();
    }, [step, code.length]);

    // Восстановление caret для телефона
    useLayoutEffect(() => {
        const el = phoneInputRef.current;
        const pos = pendingCaretPosRef.current;
        if (!el || pos === null) return;
        try {
            el.setSelectionRange(pos, pos);
        } catch {
            // ignore
        } finally {
            pendingCaretPosRef.current = null;
        }
    }, [formattedPhone]);

    const validatePhone = useCallback(
        (nextNational: string): string => {
            if (!touchedPhone) return '';
            if (!nextNational.length) return '';
            if (nextNational.length !== 10) return 'Введите номер телефона полностью';
            return '';
        },
        [touchedPhone],
    );

    const applyNextPhoneValue = (nextInputValue: string, caretPos: number) => {
        const { national: nextNational, nationalCaretIndex } = parsePhoneFromInput(nextInputValue, caretPos);
        const trimmed = nextInputValue.trim();
        const digitsAll = digitsOnly(nextInputValue);
        const firstDigit = digitsAll[0] ?? '';
        const startsPlus = trimmed.startsWith('+');
        let nextPrefixMode: PrefixMode = 'none';
        if (!nextNational.length) {
            if (startsPlus) {
                if (!digitsAll.length) nextPrefixMode = 'plus';
                else if (firstDigit === '7' || firstDigit === '8') nextPrefixMode = 'country';
                else nextPrefixMode = 'plus';
            } else {
                if (firstDigit === '8' || firstDigit === '7') nextPrefixMode = 'country';
            }
        }
        setNational(nextNational);
        setPrefixMode(nextNational.length ? 'none' : nextPrefixMode);
        if (touchedPhone) {
            const err = validatePhone(nextNational);
            setPhoneError(err);
            if (!err) setPhoneStatus(null);
        }
        const nextFormatted = nextNational ? formatRuPhone(nextNational) : nextPrefixMode === 'country' ? '+7 ' : nextPrefixMode === 'plus' ? '+' : '';
        const nextCaretPos = nextNational.length
            ? caretPosFromNationalIndex(nextFormatted, nationalCaretIndex)
            : nextFormatted === '+'
              ? 1
              : nextFormatted.startsWith('+7')
                ? 3
                : 0;
        pendingCaretPosRef.current = nextCaretPos;
    };

    const onChangePhone: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        const input = e.currentTarget;
        const nextValue = input.value;
        const caretPos = input.selectionStart ?? nextValue.length;
        applyNextPhoneValue(nextValue, caretPos);
    };

    const onKeyDownPhone: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
        const ne = e.nativeEvent as KeyboardEvent;
        if (ne.isComposing) return;
        const input = e.currentTarget;
        const value = input.value;
        const selStart = input.selectionStart ?? 0;
        const selEnd = input.selectionEnd ?? selStart;
        // Первый символ "+" - показываем "+"
        if (!value && !national && prefixMode === 'none' && e.key === '+') {
            e.preventDefault();
            setPrefixMode('plus');
            pendingCaretPosRef.current = 1;
            return;
        }
        // Первый символ "8" - показываем "+7 "
        if (!value && !national && prefixMode === 'none' && e.key === '8') {
            e.preventDefault();
            setPrefixMode('country');
            pendingCaretPosRef.current = 3;
            return;
        }
        // Первый символ "7" - сразу "+7 "
        if (!value && !national && prefixMode === 'none' && e.key === '7') {
            e.preventDefault();
            setPrefixMode('country');
            pendingCaretPosRef.current = 3;
            return;
        }
        // Backspace/Delete по цифрам внутри маски
        if ((e.key === 'Backspace' || e.key === 'Delete') && selStart === selEnd) {
            const { national: curNational, nationalCaretIndex } = parsePhoneFromInput(value, selStart);
            // Удаление префикса, когда национальных цифр нет
            if (!curNational.length) {
                if (e.key === 'Backspace') {
                    if (value === '+') {
                        e.preventDefault();
                        setPrefixMode('none');
                        pendingCaretPosRef.current = 0;
                        return;
                    }
                    if (value.startsWith('+7') && selStart > 1) {
                        e.preventDefault();
                        setPrefixMode('plus');
                        pendingCaretPosRef.current = 1;
                        return;
                    }
                }
                if (e.key === 'Delete') {
                    if (value === '+' || value.startsWith('+7')) {
                        e.preventDefault();
                        setPrefixMode('none');
                        pendingCaretPosRef.current = 0;
                        return;
                    }
                }
                e.preventDefault();
                return;
            }
            if (e.key === 'Backspace') {
                if (nationalCaretIndex <= 0) {
                    e.preventDefault();
                    return;
                }
                const delAt = nationalCaretIndex - 1;
                const nextNational = curNational.slice(0, delAt) + curNational.slice(delAt + 1);
                setNational(nextNational);
                setPrefixMode(nextNational.length ? 'none' : 'country');
                const nextFormatted = nextNational ? formatRuPhone(nextNational) : '+7 ';
                pendingCaretPosRef.current = nextNational ? caretPosFromNationalIndex(nextFormatted, delAt) : 3;
                e.preventDefault();
                return;
            }
            if (e.key === 'Delete') {
                if (nationalCaretIndex >= curNational.length) {
                    e.preventDefault();
                    return;
                }
                const delAt = nationalCaretIndex;
                const nextNational = curNational.slice(0, delAt) + curNational.slice(delAt + 1);
                setNational(nextNational);
                setPrefixMode(nextNational.length ? 'none' : 'country');
                const nextFormatted = nextNational ? formatRuPhone(nextNational) : '+7 ';
                pendingCaretPosRef.current = nextNational ? caretPosFromNationalIndex(nextFormatted, delAt) : 3;
                e.preventDefault();
                return;
            }
        }
    };

    const onBlurPhone = () => {
        setTouchedPhone(true);
        const err = validatePhone(national);
        setPhoneError(err);
    };

    const focusOtp = () => {
        window.setTimeout(() => {
            const el = otpInputRef.current;
            if (!el) return;
            el.focus();
            try {
                const len = el.value.length;
                el.setSelectionRange(len, len);
            } catch {
                // ignore
            }
        }, 0);
    };

    const requestCode = useCallback(async () => {
        if (!phoneE164) throw new Error('Неверный номер телефона');
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;
        const csrfToken = await getCsrfToken();
        const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/auth/request-code`, {
            method: 'POST',
            credentials: 'include',
            signal: controller.signal,
            headers: {
                'Content-Type': 'application/json',
                'csrf-token': csrfToken,
            },
            body: JSON.stringify({ phone: phoneE164, channel: CHANNEL }),
        });
        let data: any = null;
        try {
            data = await response.json();
        } catch {
            data = null;
        }
        if (!response.ok) {
            const msg = typeof data?.message === 'string' ? data.message : '';
            const safe = msg ? redactOtpLike(msg) : 'Ошибка отправки кода';
            throw new Error(safe);
        }
        const msgRaw = typeof data?.message === 'string' ? data.message : '';
        const msg = IS_DEV ? msgRaw : redactOtpLike(msgRaw);
        return msg;
    }, [phoneE164]);

    const verifyCode = useCallback(async () => {
        if (!phoneE164) throw new Error('Неверный номер телефона');
        if (code.length !== OTP_LEN) throw new Error('Введите код полностью');
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;
        const csrfToken = await getCsrfToken();
        const favoritesData = JSON.parse(localStorage.getItem('wishlist') || '[]');
        const cartData = JSON.parse(localStorage.getItem('cart') || '[]');
        const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/auth/verify-code`, {
            method: 'POST',
            credentials: 'include',
            signal: controller.signal,
            headers: {
                'Content-Type': 'application/json',
                'csrf-token': csrfToken,
            },
            body: JSON.stringify({
                phone: phoneE164,
                code,
                channel: CHANNEL,
                favorites: favoritesData,
                cart: cartData,
            }),
        });
        let data: any = null;
        try {
            data = await response.json();
        } catch {
            data = null;
        }
        if (!response.ok) {
            const msg = typeof data?.message === 'string' ? data.message : '';
            const safe = msg ? redactOtpLike(msg) : 'Ошибка ввода';
            throw new Error(safe);
        }
        return data;
    }, [phoneE164, code]);

    const onResend = async () => {
        if (busy !== 'none') return;
        if (resendTimer > 0) return;
        if (!phoneE164) return;
        setCode('');
        setCodeError('');
        setPhoneStatus(null);
        setBusy('resend');
        try {
            setDevCodeHint('');
            const serverMessage = await requestCode();
            if (IS_DEV && serverMessage) setDevCodeHint(serverMessage);
            setResendTimer(RESEND_SECONDS);
            // остаёмся на шаге кода
            focusOtp();
        } catch (err: any) {
            const msg = typeof err?.message === 'string' && err.message.trim() ? err.message.trim() : 'Ошибка сервера';
            setCodeError(msg);
        } finally {
            setBusy('none');
        }
    };

    const onChangeCode: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        const raw = digitsOnly(e.target.value).slice(0, OTP_LEN);
        setCode(raw);
        if (codeError) setCodeError('');
    };

    const onPasteCode: React.ClipboardEventHandler<HTMLInputElement> = (e) => {
        const text = e.clipboardData.getData('text') || '';
        const raw = digitsOnly(text).slice(0, OTP_LEN);
        if (!raw) return;
        e.preventDefault();
        setCode(raw);
        if (codeError) setCodeError('');
    };

    const onKeyDownCode: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
        const ne = e.nativeEvent as KeyboardEvent;
        if (ne.isComposing) return;
        // Держим курсор в конце
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'Home' || e.key === 'End') {
            e.preventDefault();
            const el = e.currentTarget;
            const len = el.value.length;
            try {
                el.setSelectionRange(len, len);
            } catch {
                // ignore
            }
        }
    };

    const onFocusCode: React.FocusEventHandler<HTMLInputElement> = (e) => {
        setOtpFocused(true);
        const el = e.currentTarget;
        const len = el.value.length;
        try {
            el.setSelectionRange(len, len);
        } catch {
            // ignore
        }
    };

    const onBlurCode: React.FocusEventHandler<HTMLInputElement> = () => {
        setOtpFocused(false);
    };

    const onSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
        e.preventDefault();
        if (step === 'phone') {
            setTouchedPhone(true);
            setPhoneStatus(null);
            if (!national.length) {
                setPhoneError('Введите номер телефона');
                return;
            }
            if (national.length !== 10) {
                setPhoneError('Введите номер телефона полностью');
                return;
            }
            if (!agree) {
                setPhoneError('');
                setPhoneStatus({ kind: 'error', text: 'Подтвердите согласие с условиями' });
                return;
            }
            if (resendTimer > 0) {
                setPhoneError('');
                setPhoneStatus({ kind: 'info', text: `Повторный запрос будет доступен через ${formatTimer(resendTimer)}` });
                return;
            }
            setPhoneError('');
            setBusy('request');
            try {
                setDevCodeHint('');
                const serverMessage = await requestCode();
                if (IS_DEV && serverMessage) setDevCodeHint(serverMessage);
                setResendTimer(RESEND_SECONDS);
                setCode('');
                setCodeError('');
                setStep('code');
                try {
                    await onRequestCode?.(phoneE164);
                } catch {
                    // noop
                }
            } catch (err: any) {
                const msg = typeof err?.message === 'string' && err.message.trim() ? err.message.trim() : 'Ошибка сервера';
                setPhoneStatus({ kind: 'error', text: msg });
            } finally {
                setBusy('none');
            }
            return;
        }
        // step === 'code'
        setCodeError('');
        if (!agree) {
            setCodeError('Подтвердите согласие с условиями');
            return;
        }
        if (code.length !== OTP_LEN) {
            setCodeError('Введите код полностью');
            return;
        }
        setBusy('verify');
        try {
            const data = await verifyCode();
            // Устанавливаем пользователя в store
            // dispatch(setUser(data.user));
            const sessionUser = await dispatch(fetchUserSession()).unwrap();
            dispatch(getFromLocalStorage('wishlist'));
            dispatch(getFromLocalStorage('cart'));
            dispatch(clearPersonalDiscounts());
            dispatch(fetchProducts());
            // 1) если логин из корзины — остаёмся в /cart
            const isCart = location.pathname === '/cart' || location.pathname.startsWith('/cart/');
            if (isCart) {
                onClose();
                return;
            }
            // 2) если бэк вернул /catalog и профиль полный - закрываем popup
            const redirectRaw = typeof data?.redirect === 'string' ? data.redirect.trim() : '';
            const isCatalogRedirect =
                redirectRaw === '/catalog' || redirectRaw.startsWith('/catalog?') || redirectRaw.startsWith('/catalog#') || redirectRaw.startsWith('/catalog/');
            const complete = isProfileComplete(sessionUser);
            if (isCatalogRedirect && complete) {
                onClose();
                return;
            }
            // 3) иначе - ведём в ЛК заполнять/проверять данные
            navigate('/account', { replace: true });
            onClose();
            return;
        } catch (err: any) {
            const rawMsg = typeof err?.message === 'string' && err.message.trim() ? err.message.trim() : 'Ошибка ввода';
            const msgLower = rawMsg.toLowerCase();
            if (msgLower.includes('истек') || msgLower.includes('истёк') || msgLower.includes('expired')) {
                setCodeError('Код истёк');
            } else {
                setCodeError('Ошибка ввода');
            }
        } finally {
            setBusy('none');
        }
    };

    const otpDigits = useMemo(() => {
        const arr: string[] = [];
        for (let i = 0; i < OTP_LEN; i++) arr.push(code[i] ?? '');
        return arr;
    }, [code]);

    const resendText = useMemo(() => `Получить новый код можно через ${formatTimer(resendTimer)}`, [resendTimer]);

    return (
        <form className="authPopupContent" onSubmit={onSubmit} noValidate>
            <div className="authPopupContent__head">
                <h1 className="authPopupContent__title">Вход или регистрация</h1>
                <button className="authPopupContent__close" type="button" onClick={onClose} aria-label="Закрыть">
                    <img className="authPopupContent__closeIcon" src="/icons/new/close.svg" alt="" aria-hidden="true" />
                </button>
            </div>
            {step === 'phone' ? (
                <>
                    <p className="authPopupContent__subtitle">Введите номер телефона для получения SMS с кодом для входа в личный кабинет</p>
                    <div className="authPopupContent__field">
                        <label className="authPopupContent__label" htmlFor="authPhone">
                            Номер телефона
                        </label>
                        <input
                            ref={phoneInputRef}
                            id="authPhone"
                            className={`authPopupContent__input${phoneError ? ' authPopupContent__input_error' : ''}`}
                            type="tel"
                            inputMode="tel"
                            autoComplete="tel"
                            placeholder="+7 000 000 00 00"
                            value={formattedPhone}
                            onChange={onChangePhone}
                            onKeyDown={onKeyDownPhone}
                            onBlur={onBlurPhone}
                            aria-invalid={phoneError ? true : undefined}
                        />
                        {phoneError ? <div className="authPopupContent__error">{phoneError}</div> : <div className="authPopupContent__errorSpacer" />}
                    </div>
                    <div className="authPopupContent__spacer" />
                    <div className="authPopupContent__footer">
                        {phoneStatus ? (
                            <div
                                className={`authPopupContent__status authPopupContent__status_${phoneStatus.kind}`}
                                role={phoneStatus.kind === 'error' ? 'alert' : 'status'}
                                aria-live="polite"
                            >
                                {phoneStatus.text}
                            </div>
                        ) : null}
                        <label className="authPopupContent__agree">
                            <input className="authPopupContent__checkbox" type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} />
                            <span className="authPopupContent__checkboxBox" aria-hidden="true" />
                            <span className="authPopupContent__agreeText">
                                Согласен с условиями{' '}
                                <Link className="authPopupContent__agreeLink" to="/customers?section=policy" target="_blank" rel="noopener noreferrer">
                                    обработки данных
                                </Link>
                                ,{' '}
                                <Link className="authPopupContent__agreeLink" to="/customers?section=return" target="_blank" rel="noopener noreferrer">
                                    пользовательского соглашения
                                </Link>
                                , и{' '}
                                <Link className="authPopupContent__agreeLink" to="/customers?section=offer" target="_blank" rel="noopener noreferrer">
                                    оферты
                                </Link>
                            </span>
                        </label>
                        <button className="authPopupContent__submit" type="submit" disabled={!canSubmitPhone} aria-disabled={!canSubmitPhone}>
                            {busy === 'request' ? 'Отправка...' : 'Получить код'}
                        </button>
                        {resendTimer > 0 ? (
                            <div className="authPopupContent__hint" aria-live="polite">
                                Можно запросить код ещё раз через <span className="authPopupContent__hintTimer">{formatTimer(resendTimer)}</span>
                            </div>
                        ) : (
                            <div className="authPopupContent__hintSpacer" />
                        )}
                    </div>
                </>
            ) : (
                <>
                    <p className="authPopupContent__subtitle authPopupContent__subtitle_code">
                        Введите код из SMS, отправленного <br className="authPopupContent__subtitle_code_br" />
                        на номер {phoneForSubtitle}
                    </p>
                    <div className="authPopupContent__otpContainer">
                        <div className="authPopupContent__otp" aria-label="Ввод кода из SMS">
                            <div
                                className={[
                                    'authPopupContent__otpBoxes',
                                    otpFocused ? 'authPopupContent__otpBoxes_focus' : '',
                                    codeError ? 'authPopupContent__otpBoxes_error' : '',
                                ]
                                    .filter(Boolean)
                                    .join(' ')}
                                aria-hidden="true"
                            >
                                {otpDigits.map((d, idx) => (
                                    <div key={idx} className="authPopupContent__otpBox">
                                        {d ? <span className="authPopupContent__otpDigit">{d}</span> : <span className="authPopupContent__otpDash">_</span>}
                                    </div>
                                ))}
                            </div>
                            <input
                                ref={otpInputRef}
                                className="authPopupContent__otpInput"
                                type="tel"
                                inputMode="numeric"
                                pattern="\d*"
                                autoComplete="one-time-code"
                                name="one-time-code"
                                enterKeyHint="done"
                                autoCorrect="off"
                                autoCapitalize="none"
                                spellCheck={false}
                                aria-label="Код из SMS"
                                value={code}
                                onChange={onChangeCode}
                                onPaste={onPasteCode}
                                onKeyDown={onKeyDownCode}
                                onFocus={onFocusCode}
                                onBlur={onBlurCode}
                            />
                        </div>
                        <div className="authPopupContent__otpMeta">
                            {codeError ? (
                                <div className="authPopupContent__otpError" role="alert" aria-live="polite">
                                    {codeError}
                                </div>
                            ) : resendTimer > 0 ? (
                                <div className="authPopupContent__otpTimer" aria-live="polite">
                                    {resendText}
                                </div>
                            ) : (
                                <div className="authPopupContent__otpMetaSpacer" />
                            )}
                            <button
                                className={`authPopupContent__resendLink${resendTimer > 0 || busy !== 'none' ? ' authPopupContent__resendLink_disabled' : ''}`}
                                type="button"
                                onClick={onResend}
                                disabled={resendTimer > 0 || busy !== 'none'}
                            >
                                {busy === 'resend' ? 'Отправка...' : 'Отправить еще раз'}
                            </button>
                            {IS_DEV && devCodeHint ? (
                                <div className="authPopupContent__devCode" aria-live="polite">
                                    {devCodeHint}
                                </div>
                            ) : null}
                        </div>
                    </div>
                    <div className="authPopupContent__spacer" />
                    <div className="authPopupContent__footer">
                        <label className="authPopupContent__agree">
                            <input className="authPopupContent__checkbox" type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} />
                            <span className="authPopupContent__checkboxBox" aria-hidden="true" />
                            <span className="authPopupContent__agreeText">Согласен с условиями обработки данных, пользовательского соглашения и оферты</span>
                        </label>
                        <button className="authPopupContent__submit" type="submit" disabled={!canSubmitCode} aria-disabled={!canSubmitCode}>
                            {busy === 'verify' ? 'Проверка...' : 'Подтвердить код'}
                        </button>
                    </div>
                </>
            )}
        </form>
    );
};

export default AuthPopupContent;
